// Parametric warehouse-object definitions.
// All linear dimensions in millimetres. (1 unit in 3D = 1 metre, so we scale /1000.)

// Each category belongs to a module: 'warehouse', 'supermarket', or 'shared' (shows in both).
export const CATEGORIES = [
  { id: "racks", label: "Racking", module: "warehouse" },
  { id: "mezzanine", label: "Mezzanine", module: "warehouse" },
  { id: "mhe", label: "Material Handling", module: "warehouse" },
  { id: "super_racks", label: "Shelving & Racks", module: "supermarket" },
  { id: "super_cold", label: "Cold Storage", module: "supermarket" },
  { id: "super_pos", label: "Checkout & Signage", module: "supermarket" },
  { id: "super_misc", label: "Trolleys & Bins", module: "supermarket" },
  { id: "obstacles", label: "Obstacles", module: "shared" },
];

export const OBJECT_DEFS = {
  pallet_rack: {
    type: "pallet_rack",
    category: "racks",
    label: "Pallet Rack",
    description: "Selective pallet racking with adjustable beams",
    color: "#f97316",
    defaults: {
      height: 6000,
      depth: 1100,
      bay_length: 2700,
      num_bays: 4,
      levels: 4,
      beam_spacing: 1500,
      back_to_back: false,
      aisle_width: 2800,
      load_per_level: 1500,
      label: "PR",
    },
    schema: [
      { key: "height", label: "Height", min: 1500, max: 12000, step: 100, unit: "mm" },
      { key: "depth", label: "Depth", min: 600, max: 1500, step: 50, unit: "mm" },
      { key: "bay_length", label: "Bay Length", min: 1200, max: 4500, step: 100, unit: "mm" },
      { key: "num_bays", label: "Bays", min: 1, max: 30, step: 1 },
      { key: "levels", label: "Levels", min: 2, max: 12, step: 1 },
      { key: "beam_spacing", label: "Beam Spacing", min: 800, max: 2500, step: 50, unit: "mm" },
      { key: "back_to_back", label: "Back-to-Back", type: "boolean" },
      { key: "aisle_width", label: "Aisle Width", min: 1500, max: 5000, step: 100, unit: "mm" },
      { key: "load_per_level", label: "Load / Level", min: 200, max: 5000, step: 50, unit: "kg" },
      { key: "label", label: "Row Label", type: "text" },
    ],
  },
  slotted_angle_rack: {
    type: "slotted_angle_rack",
    category: "racks",
    label: "Slotted Angle Rack",
    color: "#3b82f6",
    defaults: {
      height: 2000,
      width: 1200,
      depth: 600,
      shelves: 5,
      back_panel: false,
      double_sided: false,
      units_in_row: 1,
    },
    schema: [
      { key: "height", label: "Height", min: 900, max: 3000, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 600, max: 2000, step: 50, unit: "mm" },
      { key: "depth", label: "Depth", min: 300, max: 900, step: 50, unit: "mm" },
      { key: "shelves", label: "Shelves", min: 2, max: 8, step: 1 },
      { key: "back_panel", label: "Closed Back", type: "boolean" },
      { key: "double_sided", label: "Double-side Access", type: "boolean" },
      { key: "units_in_row", label: "Units in Row", min: 1, max: 12, step: 1 },
    ],
  },
  drive_in_rack: {
    type: "drive_in_rack",
    category: "racks",
    label: "Drive-In Rack",
    color: "#f97316",
    defaults: {
      height: 7000,
      depth_pallets: 5,
      pallet_depth: 1100,
      levels: 4,
      lanes: 3,
      lane_width: 1300,
      load_per_level: 1200,
    },
    schema: [
      { key: "height", label: "Height", min: 2500, max: 12000, step: 100, unit: "mm" },
      { key: "depth_pallets", label: "Pallets Deep", min: 2, max: 12, step: 1 },
      { key: "pallet_depth", label: "Pallet Depth", min: 800, max: 1500, step: 50, unit: "mm" },
      { key: "levels", label: "Levels", min: 2, max: 12, step: 1 },
      { key: "lanes", label: "Lanes", min: 1, max: 10, step: 1 },
      { key: "lane_width", label: "Lane Width", min: 1100, max: 1600, step: 50, unit: "mm" },
      { key: "load_per_level", label: "Load / Level", min: 200, max: 5000, step: 50, unit: "kg" },
    ],
  },
  cantilever_rack: {
    type: "cantilever_rack",
    category: "racks",
    label: "Cantilever Rack",
    color: "#f97316",
    defaults: {
      height: 4000,
      arm_length: 1200,
      levels: 4,
      column_spacing: 1500,
      columns: 4,
      double_sided: false,
      load_per_arm: 600,
    },
    schema: [
      { key: "height", label: "Height", min: 1500, max: 8000, step: 100, unit: "mm" },
      { key: "arm_length", label: "Arm Length", min: 600, max: 2400, step: 100, unit: "mm" },
      { key: "levels", label: "Arm Levels", min: 2, max: 8, step: 1 },
      { key: "column_spacing", label: "Column Spacing", min: 1000, max: 2500, step: 100, unit: "mm" },
      { key: "columns", label: "Columns", min: 2, max: 16, step: 1 },
      { key: "double_sided", label: "Double-Sided", type: "boolean" },
      { key: "load_per_arm", label: "Load / Arm", min: 100, max: 3000, step: 50, unit: "kg" },
    ],
  },
  mezzanine: {
    type: "mezzanine",
    category: "mezzanine",
    label: "Mezzanine Floor",
    color: "#eab308",
    defaults: {
      shape: "rectangle", // rectangle | l_shape
      length: 8000,
      width: 6000,
      notch_length: 3000,
      notch_width: 2500,
      notch_corner: "front_right", // front_left | front_right | back_left | back_right
      floor_height: 2500,
      slab_thickness: 200,
      column_grid: 3000,
      railing: true,
      stair_position: "front_left",
      stair_direction: "outward", // outward | along_edge
      stair_landing: true,
      stair_width: 1400,
      stair_handrails: false,
      load_capacity: 500,
    },
    schema: [
      {
        key: "shape",
        label: "Shape",
        type: "select",
        options: [
          ["rectangle", "Rectangle"],
          ["l_shape", "L-Shape"],
        ],
      },
      { key: "length", label: "Length", min: 2000, max: 30000, step: 100, unit: "mm" },
      { key: "width", label: "Width", min: 2000, max: 30000, step: 100, unit: "mm" },
      { key: "notch_length", label: "Notch Length", min: 500, max: 15000, step: 100, unit: "mm", showIf: (p) => p.shape === "l_shape" },
      { key: "notch_width", label: "Notch Width", min: 500, max: 15000, step: 100, unit: "mm", showIf: (p) => p.shape === "l_shape" },
      {
        key: "notch_corner",
        label: "Notch Corner",
        type: "select",
        showIf: (p) => p.shape === "l_shape",
        options: [
          ["front_left", "Front Left"],
          ["front_right", "Front Right"],
          ["back_left", "Back Left"],
          ["back_right", "Back Right"],
        ],
      },
      { key: "floor_height", label: "Floor Height", min: 2000, max: 6000, step: 100, unit: "mm" },
      { key: "slab_thickness", label: "Slab Thickness", min: 80, max: 300, step: 10, unit: "mm" },
      { key: "column_grid", label: "Column Grid", min: 2000, max: 6000, step: 100, unit: "mm" },
      { key: "railing", label: "Railing", type: "boolean" },
      {
        key: "stair_position",
        label: "Stair Position",
        type: "stair_picker",
        options: [
          ["front_left", "Front Left"],
          ["front_center", "Front Center"],
          ["front_right", "Front Right"],
          ["back_left", "Back Left"],
          ["back_center", "Back Center"],
          ["back_right", "Back Right"],
          ["side_left", "Side Left"],
          ["side_right", "Side Right"],
        ],
      },
      {
        key: "stair_direction",
        label: "Stair Direction",
        type: "select",
        options: [
          ["outward", "Outward from edge"],
          ["along_edge", "Along edge"],
        ],
      },
      { key: "stair_width", label: "Stair Width", min: 800, max: 3000, step: 50, unit: "mm" },
      { key: "stair_landing", label: "Landing at Top", type: "boolean" },
      { key: "stair_handrails", label: "Handrails", type: "boolean" },
      { key: "load_capacity", label: "Load (kg/m²)", min: 200, max: 1500, step: 50, unit: "kg/m²" },
    ],
  },
  forklift: {
    type: "forklift",
    category: "mhe",
    label: "Forklift",
    color: "#f59e0b",
    defaults: { length: 2400, width: 1100, height: 2200 },
    schema: [
      { key: "length", label: "Length", min: 1800, max: 3500, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 900, max: 1500, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 1800, max: 3000, step: 50, unit: "mm" },
    ],
  },
  reach_truck: {
    type: "reach_truck",
    category: "mhe",
    label: "Reach Truck",
    color: "#fb923c",
    defaults: { length: 2200, width: 1100, height: 2500 },
    schema: [
      { key: "length", label: "Length", min: 1800, max: 3000, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 900, max: 1500, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 1800, max: 3000, step: 50, unit: "mm" },
    ],
  },
  hand_pallet_truck: {
    type: "hand_pallet_truck",
    category: "mhe",
    label: "Pallet Truck",
    color: "#fbbf24",
    defaults: { length: 1500, width: 550, height: 1200 },
    schema: [
      { key: "length", label: "Length", min: 1100, max: 2000, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 450, max: 800, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 800, max: 1500, step: 50, unit: "mm" },
    ],
  },
  vna_truck: {
    type: "vna_truck",
    category: "mhe",
    label: "VNA / Turret Truck",
    description: "Very-narrow-aisle truck with rotating fork carriage",
    color: "#f97316",
    defaults: { length: 3000, width: 1200, height: 3200, lift_height: 12000, capacity: 1500 },
    schema: [
      { key: "length", label: "Length", min: 2500, max: 4000, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 1100, max: 1600, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 2500, max: 3600, step: 50, unit: "mm" },
      { key: "lift_height", label: "Lift Height", min: 6000, max: 15000, step: 100, unit: "mm" },
      { key: "capacity", label: "Capacity", min: 800, max: 2500, step: 100, unit: "kg" },
    ],
  },
  order_picker: {
    type: "order_picker",
    category: "mhe",
    label: "Order Picker",
    description: "Man-up picking truck for case-pick from racks",
    color: "#fb923c",
    defaults: { length: 2400, width: 1100, height: 2400, lift_height: 6000, capacity: 500 },
    schema: [
      { key: "length", label: "Length", min: 1800, max: 3200, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 900, max: 1400, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 2200, max: 3000, step: 50, unit: "mm" },
      { key: "lift_height", label: "Lift Height", min: 3000, max: 12000, step: 100, unit: "mm" },
      { key: "capacity", label: "Capacity", min: 200, max: 1200, step: 100, unit: "kg" },
    ],
  },
  walkie_stacker: {
    type: "walkie_stacker",
    category: "mhe",
    label: "Walkie Stacker",
    description: "Powered pedestrian stacker (electric)",
    color: "#facc15",
    defaults: { length: 1800, width: 900, height: 2100, lift_height: 3000, capacity: 1200 },
    schema: [
      { key: "length", label: "Length", min: 1500, max: 2400, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 700, max: 1200, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 1800, max: 2400, step: 50, unit: "mm" },
      { key: "lift_height", label: "Lift Height", min: 1600, max: 5500, step: 100, unit: "mm" },
      { key: "capacity", label: "Capacity", min: 500, max: 2000, step: 100, unit: "kg" },
    ],
  },
  tow_tractor: {
    type: "tow_tractor",
    category: "mhe",
    label: "Tow Tractor",
    description: "Tugger for milk-run trolley trains",
    color: "#eab308",
    defaults: { length: 1900, width: 850, height: 1600, tow_capacity: 3000 },
    schema: [
      { key: "length", label: "Length", min: 1500, max: 3000, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 700, max: 1200, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 1300, max: 2000, step: 50, unit: "mm" },
      { key: "tow_capacity", label: "Tow Capacity", min: 1000, max: 10000, step: 500, unit: "kg" },
    ],
  },
  scissor_lift: {
    type: "scissor_lift",
    category: "mhe",
    label: "Scissor Lift",
    description: "Mobile elevated work platform",
    color: "#f59e0b",
    defaults: { length: 2500, width: 1200, height: 2000, lift_height: 8000, capacity: 300 },
    schema: [
      { key: "length", label: "Length", min: 1800, max: 3500, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 800, max: 1800, step: 50, unit: "mm" },
      { key: "height", label: "Height (stowed)", min: 1500, max: 2400, step: 50, unit: "mm" },
      { key: "lift_height", label: "Lift Height", min: 4000, max: 16000, step: 500, unit: "mm" },
      { key: "capacity", label: "Platform Load", min: 200, max: 800, step: 50, unit: "kg" },
    ],
  },
  roller_conveyor: {
    type: "roller_conveyor",
    category: "mhe",
    label: "Roller Conveyor",
    description: "Gravity / powered conveyor segment",
    color: "#d97706",
    defaults: { length: 3000, width: 600, height: 700, powered: false },
    schema: [
      { key: "length", label: "Length", min: 1000, max: 12000, step: 100, unit: "mm" },
      { key: "width", label: "Width", min: 400, max: 1200, step: 50, unit: "mm" },
      { key: "height", label: "Deck Height", min: 400, max: 1200, step: 50, unit: "mm" },
      { key: "powered", label: "Powered", type: "boolean" },
    ],
  },
  pallet: {
    type: "pallet",
    category: "mhe",
    label: "Pallet",
    color: "#a3a3a3",
    defaults: { length: 1200, width: 1000, height: 150 },
    schema: [
      { key: "length", label: "Length", min: 800, max: 1500, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 800, max: 1200, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 100, max: 200, step: 10, unit: "mm" },
    ],
  },
  pillar: {
    type: "pillar",
    category: "obstacles",
    label: "Pillar / Column",
    color: "#737373",
    defaults: { length: 500, width: 500, height: 6000 },
    schema: [
      { key: "length", label: "Size X", min: 200, max: 1500, step: 50, unit: "mm" },
      { key: "width", label: "Size Y", min: 200, max: 1500, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 1000, max: 12000, step: 100, unit: "mm" },
    ],
  },
  office_block: {
    type: "office_block",
    category: "obstacles",
    label: "Office Block",
    color: "#64748b",
    defaults: { length: 6000, width: 4000, height: 3000 },
    schema: [
      { key: "length", label: "Length", min: 2000, max: 20000, step: 100, unit: "mm" },
      { key: "width", label: "Width", min: 2000, max: 20000, step: 100, unit: "mm" },
      { key: "height", label: "Height", min: 2400, max: 6000, step: 100, unit: "mm" },
    ],
  },
  loading_dock: {
    type: "loading_dock",
    category: "obstacles",
    label: "Loading Dock",
    color: "#06b6d4",
    defaults: { length: 3000, width: 1500, height: 200 },
    schema: [
      { key: "length", label: "Length", min: 1500, max: 6000, step: 100, unit: "mm" },
      { key: "width", label: "Width", min: 1000, max: 3000, step: 100, unit: "mm" },
      { key: "height", label: "Height", min: 100, max: 1500, step: 50, unit: "mm" },
    ],
  },

  // -------------------- SUPERMARKET MODULE --------------------
  wall_rack: {
    type: "wall_rack",
    category: "super_racks",
    label: "Wall Rack",
    description: "Perimeter shelving against the wall",
    color: "#0ea5e9",
    defaults: {
      height: 2100,
      width: 1200,
      depth: 500,
      shelves: 5,
      bays: 1,
      back_panel: "solid", // solid | pegboard | slatwall
    },
    schema: [
      { key: "height", label: "Height", min: 1500, max: 2700, step: 50, unit: "mm" },
      { key: "width", label: "Bay Width", min: 600, max: 1500, step: 50, unit: "mm" },
      { key: "depth", label: "Depth", min: 300, max: 700, step: 50, unit: "mm" },
      { key: "shelves", label: "Shelves", min: 3, max: 8, step: 1 },
      { key: "bays", label: "Bays", min: 1, max: 20, step: 1 },
      {
        key: "back_panel",
        label: "Back Panel",
        type: "select",
        options: [
          ["solid", "Solid Back"],
          ["pegboard", "Pegboard"],
          ["slatwall", "Slatwall"],
        ],
      },
    ],
  },
  gondola_rack: {
    type: "gondola_rack",
    category: "super_racks",
    label: "Gondola / Centre Rack",
    description: "Double-sided island shelving",
    color: "#14b8a6",
    defaults: {
      height: 1800,
      width: 1200,
      depth: 900, // total depth (both sides + spine)
      shelves: 5,
      bays: 3,
      double_sided: true,
      end_cap: false,
    },
    schema: [
      { key: "height", label: "Height", min: 1200, max: 2400, step: 50, unit: "mm" },
      { key: "width", label: "Bay Width", min: 600, max: 1500, step: 50, unit: "mm" },
      { key: "depth", label: "Total Depth", min: 600, max: 1200, step: 50, unit: "mm" },
      { key: "shelves", label: "Shelves / side", min: 3, max: 7, step: 1 },
      { key: "bays", label: "Bays", min: 1, max: 20, step: 1 },
      { key: "double_sided", label: "Double-sided", type: "boolean" },
      { key: "end_cap", label: "End Cap", type: "boolean" },
    ],
  },
  corner_rack: {
    type: "corner_rack",
    category: "super_racks",
    label: "Corner Rack (L)",
    description: "90° corner shelving linking two wall runs",
    color: "#0284c7",
    defaults: {
      height: 2100,
      depth: 500,
      leg_a: 1200,
      leg_b: 1200,
      shelves: 5,
      corner: "right", // left | right
    },
    schema: [
      { key: "height", label: "Height", min: 1500, max: 2700, step: 50, unit: "mm" },
      { key: "depth", label: "Depth", min: 300, max: 700, step: 50, unit: "mm" },
      { key: "leg_a", label: "Leg A", min: 600, max: 2000, step: 50, unit: "mm" },
      { key: "leg_b", label: "Leg B", min: 600, max: 2000, step: 50, unit: "mm" },
      { key: "shelves", label: "Shelves", min: 3, max: 8, step: 1 },
      {
        key: "corner",
        label: "Corner",
        type: "select",
        options: [
          ["left", "Left Corner"],
          ["right", "Right Corner"],
        ],
      },
    ],
  },
  pegboard_display: {
    type: "pegboard_display",
    category: "super_racks",
    label: "Pegboard Display",
    color: "#22d3ee",
    defaults: {
      height: 2100,
      width: 1200,
      depth: 300,
      hook_rows: 6,
      hooks_per_row: 8,
    },
    schema: [
      { key: "height", label: "Height", min: 1200, max: 2700, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 600, max: 2000, step: 50, unit: "mm" },
      { key: "depth", label: "Depth", min: 200, max: 500, step: 50, unit: "mm" },
      { key: "hook_rows", label: "Hook Rows", min: 3, max: 12, step: 1 },
      { key: "hooks_per_row", label: "Hooks / row", min: 3, max: 16, step: 1 },
    ],
  },
  checkout_counter: {
    type: "checkout_counter",
    category: "super_pos",
    label: "Checkout Counter",
    color: "#94a3b8",
    defaults: {
      length: 2200,
      width: 700,
      height: 900,
      conveyor: true,
      bagging_area: true,
      cashier_side: "left", // left | right
    },
    schema: [
      { key: "length", label: "Length", min: 1500, max: 3500, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 500, max: 900, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 800, max: 1000, step: 25, unit: "mm" },
      { key: "conveyor", label: "Conveyor Belt", type: "boolean" },
      { key: "bagging_area", label: "Bagging Area", type: "boolean" },
      {
        key: "cashier_side",
        label: "Cashier Side",
        type: "select",
        options: [["left", "Left"], ["right", "Right"]],
      },
    ],
  },
  open_chiller: {
    type: "open_chiller",
    category: "super_cold",
    label: "Open Chiller",
    description: "Open-front refrigerated display",
    color: "#38bdf8",
    defaults: { length: 2500, width: 900, height: 1800, shelves: 4 },
    schema: [
      { key: "length", label: "Length", min: 1200, max: 3600, step: 100, unit: "mm" },
      { key: "width", label: "Depth", min: 700, max: 1200, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 1500, max: 2200, step: 50, unit: "mm" },
      { key: "shelves", label: "Shelves", min: 2, max: 6, step: 1 },
    ],
  },
  glass_freezer: {
    type: "glass_freezer",
    category: "super_cold",
    label: "Glass-Door Freezer",
    color: "#60a5fa",
    defaults: { length: 1800, width: 800, height: 2000, doors: 3 },
    schema: [
      { key: "length", label: "Length", min: 900, max: 3600, step: 100, unit: "mm" },
      { key: "width", label: "Depth", min: 700, max: 1000, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 1800, max: 2200, step: 50, unit: "mm" },
      { key: "doors", label: "Doors", min: 1, max: 6, step: 1 },
    ],
  },
  chest_freezer: {
    type: "chest_freezer",
    category: "super_cold",
    label: "Chest Freezer",
    color: "#93c5fd",
    defaults: { length: 1500, width: 700, height: 900 },
    schema: [
      { key: "length", label: "Length", min: 900, max: 2400, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 600, max: 900, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 800, max: 1000, step: 25, unit: "mm" },
    ],
  },
  shopping_trolley: {
    type: "shopping_trolley",
    category: "super_misc",
    label: "Shopping Trolley",
    color: "#e2e8f0",
    defaults: { length: 950, width: 550, height: 1050 },
    schema: [
      { key: "length", label: "Length", min: 700, max: 1200, step: 50, unit: "mm" },
      { key: "width", label: "Width", min: 400, max: 700, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 900, max: 1200, step: 50, unit: "mm" },
    ],
  },
  promo_bin: {
    type: "promo_bin",
    category: "super_misc",
    label: "Promo Bin",
    description: "Round dump bin for promotions",
    color: "#f43f5e",
    defaults: { length: 900, width: 900, height: 700 },
    schema: [
      { key: "length", label: "Diameter", min: 600, max: 1500, step: 50, unit: "mm" },
      { key: "width", label: "Diameter", min: 600, max: 1500, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 600, max: 1000, step: 50, unit: "mm" },
    ],
  },
  bakery_display: {
    type: "bakery_display",
    category: "super_pos",
    label: "Bakery Display",
    color: "#fbbf24",
    defaults: { length: 1800, width: 700, height: 1300, shelves: 3 },
    schema: [
      { key: "length", label: "Length", min: 900, max: 2400, step: 100, unit: "mm" },
      { key: "width", label: "Depth", min: 500, max: 900, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 1000, max: 1500, step: 50, unit: "mm" },
      { key: "shelves", label: "Shelves", min: 2, max: 5, step: 1 },
    ],
  },
  signage_gate: {
    type: "signage_gate",
    category: "super_pos",
    label: "Entry / Exit Gate",
    color: "#a3e635",
    defaults: { length: 1500, width: 200, height: 1200 },
    schema: [
      { key: "length", label: "Width", min: 900, max: 3000, step: 100, unit: "mm" },
      { key: "width", label: "Depth", min: 150, max: 400, step: 50, unit: "mm" },
      { key: "height", label: "Height", min: 900, max: 2200, step: 50, unit: "mm" },
    ],
  },
};

export function makeObject(type, position = { x: 0, y: 0 }) {
  const def = OBJECT_DEFS[type];
  if (!def) throw new Error(`Unknown object type: ${type}`);
  return {
    id: `obj_${Math.random().toString(36).slice(2, 10)}`,
    type,
    label: def.label,
    color: def.color,
    // Rotation in degrees around each axis. Y = top-down turn (most common),
    // X = tilt forward/back, Z = tilt left/right (roll).
    rotation: { x: 0, y: 0, z: 0 },
    position, // mm in floor plane (X right, Y forward)
    locked: false,
    hidden: false,
    props: { ...def.defaults },
  };
}

// Normalise legacy rotation (a plain number stored under obj.rotation) into an
// object with x/y/z components. Old projects had rotation:number, treated as Y.
export function normaliseRotation(rot) {
  if (rot == null) return { x: 0, y: 0, z: 0 };
  if (typeof rot === "number") return { x: 0, y: rot, z: 0 };
  return { x: rot.x || 0, y: rot.y || 0, z: rot.z || 0 };
}

// Compute the axis-aligned footprint (length, width) in mm, before rotation, for an object.
export function objectFootprint(obj) {
  const p = obj.props || {};
  switch (obj.type) {
    case "pallet_rack": {
      const len = (p.bay_length || 2700) * (p.num_bays || 1);
      const dep = p.back_to_back ? (p.depth || 1100) * 2 + 100 : (p.depth || 1100);
      return { length: len, width: dep };
    }
    case "slotted_angle_rack": {
      return {
        length: (p.width || 1200) * (p.units_in_row || 1),
        width: p.double_sided ? (p.depth || 600) * 2 + 50 : (p.depth || 600),
      };
    }
    case "drive_in_rack": {
      const len = (p.lane_width || 1300) * (p.lanes || 1);
      const dep = (p.pallet_depth || 1100) * (p.depth_pallets || 1);
      return { length: len, width: dep };
    }
    case "cantilever_rack": {
      const len = (p.column_spacing || 1500) * (p.columns || 1);
      const dep = p.double_sided ? (p.arm_length || 1200) * 2 + 200 : (p.arm_length || 1200);
      return { length: len, width: dep };
    }
    case "mezzanine":
      return { length: p.length || 6000, width: p.width || 4000 };
    case "wall_rack": {
      return {
        length: (p.width || 1200) * (p.bays || 1),
        width: p.depth || 500,
      };
    }
    case "gondola_rack": {
      return {
        length: (p.width || 1200) * (p.bays || 1),
        width: p.depth || 900,
      };
    }
    case "corner_rack": {
      // Bounding box that contains the L-legs. Each leg's outward face aligns with the box edges.
      const d = p.depth || 500;
      const a = p.leg_a || 1200;
      const b = p.leg_b || 1200;
      return { length: Math.max(a, d), width: Math.max(b, d) };
    }
    case "pegboard_display": {
      return { length: p.width || 1200, width: p.depth || 300 };
    }
    default:
      return { length: p.length || 1000, width: p.width || 1000 };
  }
}


/**
 * Compliance / narrow-aisle detection.
 * Given the current rack layout and a minimum aisle-width threshold (mm),
 * return every axis-aligned aisle narrower than the threshold along with
 * the exact rectangle (mm) and gap value. Consumers render these as red
 * translucent overlays in 2D and count them in the compliance panel.
 *
 * Notes:
 *   - Only rack-like categories contribute to aisle detection (skip
 *     mezzanines, MHE, pallets, obstacles).
 *   - Requires that two rack projections overlap by at least 500mm on the
 *     other axis to count as an "aisle" (avoids false hits at corners).
 *   - Uses each object's axis-aligned bounding box from objectFootprint().
 */
export function findNarrowAisles(objects, threshold) {
  const minOverlap = 500;
  const minGap = 50;
  const boxes = (objects || [])
    .filter((o) => {
      const d = OBJECT_DEFS[o.type];
      return d && (d.category === "racks" || d.category === "super_racks");
    })
    .map((o) => {
      const fp = objectFootprint(o);
      return {
        id: o.id,
        x1: o.position.x,
        x2: o.position.x + fp.length,
        y1: o.position.y,
        y2: o.position.y + fp.width,
      };
    });
  const flagged = [];
  for (let i = 0; i < boxes.length; i++) {
    for (let j = i + 1; j < boxes.length; j++) {
      const A = boxes[i], B = boxes[j];
      const yOv = Math.max(0, Math.min(A.y2, B.y2) - Math.max(A.y1, B.y1));
      const xGap = A.x2 <= B.x1 ? B.x1 - A.x2 : A.x1 >= B.x2 ? A.x1 - B.x2 : -1;
      if (yOv >= minOverlap && xGap > minGap && xGap < threshold) {
        const y1 = Math.max(A.y1, B.y1), y2 = Math.min(A.y2, B.y2);
        const ax = A.x2 <= B.x1 ? A.x2 : B.x2;
        const bx = A.x2 <= B.x1 ? B.x1 : A.x1;
        flagged.push({
          id: `ai_${A.id}_${B.id}_x`,
          axis: "x",
          x1: ax, x2: bx, y1, y2,
          gap: xGap,
          area_mm2: xGap * yOv,
        });
      }
      const xOv = Math.max(0, Math.min(A.x2, B.x2) - Math.max(A.x1, B.x1));
      const yGap = A.y2 <= B.y1 ? B.y1 - A.y2 : A.y1 >= B.y2 ? A.y1 - B.y2 : -1;
      if (xOv >= minOverlap && yGap > minGap && yGap < threshold) {
        const x1 = Math.max(A.x1, B.x1), x2 = Math.min(A.x2, B.x2);
        const ay = A.y2 <= B.y1 ? A.y2 : B.y2;
        const by = A.y2 <= B.y1 ? B.y1 : A.y1;
        flagged.push({
          id: `ai_${A.id}_${B.id}_y`,
          axis: "y",
          x1, x2, y1: ay, y2: by,
          gap: yGap,
          area_mm2: xOv * yGap,
        });
      }
    }
  }
  return flagged;
}

// MHE presets for the Compliance panel. Values are approximate minimum
// operational aisle widths published by major manufacturers (mm).
export const MHE_PRESETS = {
  reach_truck:    { label: "Reach Truck",             threshold: 2700 },
  counterbalance: { label: "Counterbalance Forklift", threshold: 3800 },
  order_picker:   { label: "Order Picker",            threshold: 1800 },
  vna_truck:      { label: "VNA / Turret Truck",      threshold: 1650 },
  pallet_truck:   { label: "Hand Pallet Truck",       threshold: 1500 },
  custom:         { label: "Custom",                  threshold: 2500 },
};

/**
 * Detect aisles between parallel rack ROWS (not just pairs of racks).
 *
 * A "row" is a set of rack objects whose bounding boxes share the same
 * axis-aligned band on one axis (e.g., all at roughly the same Y range) and
 * whose gaps to their in-row neighbours are small (< 1500mm — think adjacent
 * racks along the same aisle wall).
 *
 * For every pair of adjacent parallel rows, we emit ONE aisle marker with:
 *   - the full X-span (or Y-span) of the overlap between the two rows
 *   - the perpendicular gap in mm
 *   - the mid-point coordinates for label placement
 *
 * Only aisles within `maxGap` mm are returned (default 6000 → typical
 * warehouse aisle upper bound). Used by Canvas2D to draw persistent labelled
 * dimension lines between rack rows.
 */
export function detectRackRowAisles(objects, maxGap = 6000) {
  const IN_ROW_GAP = 1500; // mm — max gap between consecutive racks in the same row
  const boxes = (objects || [])
    .filter((o) => {
      const d = OBJECT_DEFS[o.type];
      return d && (d.category === "racks" || d.category === "super_racks");
    })
    .map((o) => {
      const fp = objectFootprint(o);
      return {
        id: o.id,
        x1: o.position.x,
        x2: o.position.x + fp.length,
        y1: o.position.y,
        y2: o.position.y + fp.width,
        w: fp.length,
        h: fp.width,
      };
    });

  // Cluster into HORIZONTAL rows — racks that share a similar Y band. A row's
  // "band" is [min y1, max y2] across its members. Two racks belong to the
  // same row if their Y-bands overlap by ≥ 60% of the smaller box height and
  // their X-gap is < IN_ROW_GAP.
  function clusterRows(axis) {
    // axis === 'h' → cluster by Y-band (racks aligned along X)
    // axis === 'v' → cluster by X-band (racks aligned along Y)
    const items = boxes.map((b) => ({ ...b }));
    const rows = [];
    for (const it of items) {
      let added = false;
      for (const r of rows) {
        if (axis === "h") {
          const overlap = Math.max(0, Math.min(r.y2, it.y2) - Math.max(r.y1, it.y1));
          const minH = Math.min(r.y2 - r.y1, it.y2 - it.y1);
          const inline = overlap >= minH * 0.6;
          const xGap = Math.min(
            Math.abs(it.x1 - r.x2), Math.abs(r.x1 - it.x2),
            Math.abs(it.x1 - r.x1), Math.abs(it.x2 - r.x2)
          );
          if (inline && xGap < IN_ROW_GAP + it.w) {
            r.members.push(it);
            r.x1 = Math.min(r.x1, it.x1); r.x2 = Math.max(r.x2, it.x2);
            r.y1 = Math.min(r.y1, it.y1); r.y2 = Math.max(r.y2, it.y2);
            added = true; break;
          }
        } else {
          const overlap = Math.max(0, Math.min(r.x2, it.x2) - Math.max(r.x1, it.x1));
          const minW = Math.min(r.x2 - r.x1, it.x2 - it.x1);
          const inline = overlap >= minW * 0.6;
          const yGap = Math.min(
            Math.abs(it.y1 - r.y2), Math.abs(r.y1 - it.y2),
            Math.abs(it.y1 - r.y1), Math.abs(it.y2 - r.y2)
          );
          if (inline && yGap < IN_ROW_GAP + it.h) {
            r.members.push(it);
            r.x1 = Math.min(r.x1, it.x1); r.x2 = Math.max(r.x2, it.x2);
            r.y1 = Math.min(r.y1, it.y1); r.y2 = Math.max(r.y2, it.y2);
            added = true; break;
          }
        }
      }
      if (!added) rows.push({ x1: it.x1, x2: it.x2, y1: it.y1, y2: it.y2, members: [it], axis });
    }
    return rows.filter((r) => r.members.length >= 1); // include single racks too
  }

  const aisles = [];

  // Horizontal aisles — between rows stacked in Y direction.
  const hRows = clusterRows("h").sort((a, b) => a.y1 - b.y1);
  for (let i = 0; i < hRows.length; i++) {
    for (let j = i + 1; j < hRows.length; j++) {
      const A = hRows[i], B = hRows[j];
      const xOv = Math.max(0, Math.min(A.x2, B.x2) - Math.max(A.x1, B.x1));
      if (xOv < 500) continue;
      const gap = B.y1 > A.y2 ? B.y1 - A.y2 : A.y1 > B.y2 ? A.y1 - B.y2 : -1;
      if (gap <= 50 || gap > maxGap) continue;
      // Ensure no OTHER row's y-band sits inside this gap (i.e., A & B are truly adjacent)
      let interleaves = false;
      for (let k = 0; k < hRows.length; k++) {
        if (k === i || k === j) continue;
        const C = hRows[k];
        const xOv2 = Math.max(0, Math.min(A.x2, B.x2, C.x2) - Math.max(A.x1, B.x1, C.x1));
        if (xOv2 < 300) continue;
        const midY = (Math.min(A.y2, B.y2) + Math.max(A.y1, B.y1)) / 2;
        if (C.y1 < midY && C.y2 > midY) { interleaves = true; break; }
      }
      if (interleaves) continue;
      const x1 = Math.max(A.x1, B.x1);
      const x2 = Math.min(A.x2, B.x2);
      const ay = A.y2 < B.y1 ? A.y2 : B.y2;
      const by = A.y2 < B.y1 ? B.y1 : A.y1;
      aisles.push({
        id: `aisle_h_${i}_${j}`,
        axis: "y", // the DIMENSION is measured along the Y axis
        x1, x2, y1: ay, y2: by,
        gap,
      });
    }
  }

  // Vertical aisles — between rows stacked in X direction.
  const vRows = clusterRows("v").sort((a, b) => a.x1 - b.x1);
  for (let i = 0; i < vRows.length; i++) {
    for (let j = i + 1; j < vRows.length; j++) {
      const A = vRows[i], B = vRows[j];
      const yOv = Math.max(0, Math.min(A.y2, B.y2) - Math.max(A.y1, B.y1));
      if (yOv < 500) continue;
      const gap = B.x1 > A.x2 ? B.x1 - A.x2 : A.x1 > B.x2 ? A.x1 - B.x2 : -1;
      if (gap <= 50 || gap > maxGap) continue;
      let interleaves = false;
      for (let k = 0; k < vRows.length; k++) {
        if (k === i || k === j) continue;
        const C = vRows[k];
        const yOv2 = Math.max(0, Math.min(A.y2, B.y2, C.y2) - Math.max(A.y1, B.y1, C.y1));
        if (yOv2 < 300) continue;
        const midX = (Math.min(A.x2, B.x2) + Math.max(A.x1, B.x1)) / 2;
        if (C.x1 < midX && C.x2 > midX) { interleaves = true; break; }
      }
      if (interleaves) continue;
      const y1 = Math.max(A.y1, B.y1);
      const y2 = Math.min(A.y2, B.y2);
      const ax = A.x2 < B.x1 ? A.x2 : B.x2;
      const bx = A.x2 < B.x1 ? B.x1 : A.x1;
      aisles.push({
        id: `aisle_v_${i}_${j}`,
        axis: "x",
        x1: ax, x2: bx, y1, y2,
        gap,
      });
    }
  }

  return aisles;
}
